import { useMemo } from "react";

import {
  AdminPanel,
  AdminMiniList,
  AdminMiniRow,
  Banner,
} from "@/core/ui/molecules";
import { useService } from "@/core/utils/hooks";

import {
  getPlanVersionsByQuery,
  type PlanVersionsQuery,
} from "@/app/plans/data_sources/services";
import type { APIPlanVersionResponseType } from "@/core/data_sources/models/APIPlanDefinition";

export function PlanVersionsTab({
  planId,
}: {
  readonly planId: string | number;
}) {
  const { data, loading } = useService(getPlanVersionsByQuery, {
    id: planId,
    page: 1,
    page_size: 50,
  } as PlanVersionsQuery);

  const versions = useMemo<APIPlanVersionResponseType[]>(
    () => data?.data ?? [],
    [data],
  );

  if (loading && versions.length === 0) {
    return <p className="text-xs text-ink-muted">Loading versions…</p>;
  }

  return (
    <div>
      <Banner>
        Each publish creates a new immutable version snapshot. Snapshots include
        the resolved preset_version and are not mutated by later drafts.
      </Banner>
      <AdminPanel>
        <h3 className="mb-3 text-[15px] font-extrabold text-ink">
          Published Versions
        </h3>
        {versions.length === 0 ? (
          <p className="text-xs text-ink-muted">No published versions yet.</p>
        ) : (
          <AdminMiniList>
            {versions.map((v) => (
              <AdminMiniRow key={v.id}>
                <span>
                  <strong>v{v.version}</strong>
                  <span className="block text-xs text-ink-muted">
                    {v.published_at?.split("T")[0] ?? "-"} · by{" "}
                    {v.published_by ?? "-"}
                  </span>
                </span>
                <span className="text-xs text-ink-muted">
                  {v.config?.access_presets?.permission_profile_presets
                    ?.length ?? 0}{" "}
                  presets
                </span>
              </AdminMiniRow>
            ))}
          </AdminMiniList>
        )}
      </AdminPanel>
    </div>
  );
}
